<!-- app-subdomain-creators/src/Components/CreatorChatVoiceMemoRecorder.vue -->
<template>
  <!-- voice recorder overlay -->
  <transition name="fade" mode="out-in">
    <div
  v-if="show"
  class="absolute -bottom-6 flex items-center justify-center z-30 h-64 lg:w-[1300px] p-20 self-center
         bg-gradient-to-t from-zinc-900/80 to-transparent
         backdrop-blur-sm"
>

      <!-- Voice Recorder Container -->
      <div class="flex flex-col items-center">
        <!-- Voice Recorder -->
        <div
          class="flex items-center rounded-xl w-[250px] bg-zinc-800 mb-2 relative px-4 justify-between"
        >
 <!-- Centered column: waveform on top, timer below -->
  <div class="flex flex-col items-center justify-center w-full gap-1">
    <!-- waveform -->
    <div class="w-[140px] h-14 rounded-lg bg-transparent flex items-center justify-center mt-2">
      <canvas
        ref="waveformCanvas"
        class="w-full h-full rounded-lg"
      ></canvas>
    </div>

    <!-- timer -->
    <div class="text-zinc-200 text-lg font-semibold min-w-[50px] text-center">
      {{ elapsedTimeDisplay }}
    </div>
  </div>
          <!-- 🔴 mic error message -->
          <div
            v-if="micError"
            class="absolute -bottom-6 left-0 right-0 text-center text-xs text-red-400 mt-1"
          >
            {{ micError }}
          </div>

          <!-- Cancel Recording Button -->
          <div
            class="absolute -top-2 -right-2"
            @mouseenter="showCancelTooltip = true"
            @mouseleave="showCancelTooltip = false"
          >
            <button
              class="bg-zinc-200 w-6 h-6 rounded-full transition duration-200 ease-in-out transform hover:scale-105"
              type="button"
              @click="handleCancel"
            >
              <XMarkIcon class="w-4 text-zinc-800 mx-auto" />
            </button>

            <!-- Cancel Recording tooltip -->
            <div
              v-if="showCancelTooltip"
              class="absolute text-center w-32 bottom-full p-2 -translate-x-14 mb-3 bg-black opacity-80 text-white text-sm rounded-lg tooltip z-10 ease-in-out"
            >
              Cancel Recording
              <div
                class="tooltip-arrow absolute left-1/2 -right-4 mt-[6px] w-0 h-0 border-l-[12px] border-transparent border-b-[12px] transform -translate-y-1/2 rotate-45 rounded bg-black"
              ></div>
            </div>
          </div>
        </div>

        <!-- Stop Voice Recording Button -->
        <div
          class="relative"
          @mouseenter="showStopTooltip = true"
          @mouseleave="showStopTooltip = false"
        >
          <button
            class="text-zinc-200 bg-blue-500 w-10 h-10 rounded-lg transition duration-200 ease-in-out transform hover:scale-105"
            type="button"
            @click="handleStop"
          >
            <CheckIcon class="w-5 text-zinc-200 mx-auto font-bold" />
          </button>

          <!-- Stop Voice Recording tooltip -->
            <div
            v-if="showStopTooltip"
            class="absolute text-center w-28 -top-1 left-14 p-2 bg-black/60 text-white text-sm rounded-lg z-10"
            >
            Finish Recording

            <!-- arrow on LEFT middle, pointing left -->
            <div
                class="absolute top-1/2 -left-2 -translate-y-1/2
                    w-0 h-0
                    border-y-8 border-y-transparent
                    border-r-8 border-r-black/60"
            ></div>
            </div>



        </div>


      </div>

      
    </div>
  </transition>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { XMarkIcon, CheckIcon } from '@heroicons/vue/24/outline';


const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  // passed from parent so recorder can upload directly
  featureInstanceId: {
    type: [String, Number],
    default: null,
  },
  memberId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits([
  // fired when user cancels or we’re done
  'close',
  // fired when recording finishes successfully
  // payload: { url, blob, durationSeconds, key?, mime?, size? }
  'recorded',
]);

// ─────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────
const waveformCanvas = ref(null);

const audioContext = ref(null);
const analyser = ref(null);
const dataArray = ref(null);
const bufferLength = ref(0);

let mediaRecorder = null;
let mediaStream = null;
let animationId = null;

const chunks = [];
const isRecording = ref(false);

// timer
const recordingStartTime = ref(0);
const elapsedSeconds = ref(0);
let timerId = null;

const elapsedTimeDisplay = computed(() => {
  const mins = String(Math.floor(elapsedSeconds.value / 60)).padStart(2, '0');
  const secs = String(elapsedSeconds.value % 60).padStart(2, '0');
  return `${mins}:${secs}`;
});

// tooltips
const showCancelTooltip = ref(false);
const showStopTooltip = ref(false);

// mic error state
const micError = ref('');
const wasCancelled = ref(false);
// ─────────────────────────────────────────────
// Lifecycle: react to show prop
// ─────────────────────────────────────────────
watch(
  () => props.show,
  (val) => {
    if (val) {
      // opening overlay → try to start recording
      startRecording();
    } else {
      // parent hid overlay: just clean up
      cleanup();
    }
  }
);

// ─────────────────────────────────────────────
// Core recording logic
// ─────────────────────────────────────────────
const startRecording = async () => {
  if (isRecording.value) return;

  // clear previous error
  micError.value = '';

  try {
    // request mic
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    audioContext.value = new (window.AudioContext || window.webkitAudioContext)();
    analyser.value = audioContext.value.createAnalyser();
    const source = audioContext.value.createMediaStreamSource(mediaStream);

    source.connect(analyser.value);
    analyser.value.fftSize = 256;
    bufferLength.value = analyser.value.frequencyBinCount;
    dataArray.value = new Uint8Array(bufferLength.value);

    chunks.length = 0;

    mediaRecorder = new MediaRecorder(mediaStream);
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunks.push(e.data);
      }
    };
    mediaRecorder.onstop = handleRecorderStopped;

    mediaRecorder.start();
    isRecording.value = true;

    startTimer();
    await nextTick();
    startWaveformLoop();
  } catch (err) {
    console.error('[VoiceMemoRecorder] getUserMedia failed:', err);
    micError.value = 'Unable to access microphone. Check browser permissions.';
    // keep overlay open, just stop streams
    cleanupStreamsOnly();
  }
};

const stopRecordingInternal = () => {
  if (!isRecording.value) {
    // nothing actually recording; just close
    cleanup();
    emit('close');
    return;
  }

  isRecording.value = false;

  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    try {
      mediaRecorder.stop();
    } catch (e) {
      console.warn('[VoiceMemoRecorder] mediaRecorder.stop error:', e);
      cleanup();
      emit('close');
    }
  } else {
    cleanup();
    emit('close');
  }
};

const handleRecorderStopped = () => {
  stopTimer();

  // 🔒 If this stop came from “Cancel”, never emit a memo
  if (wasCancelled.value) {
    wasCancelled.value = false;   // reset for next open
    cleanup();
    emit('close');
    return;
  }

  if (!chunks.length) {
    // nothing recorded (or already cleared)
    cleanup();
    emit('close');
    return;
  }

  const blobType = 'audio/webm';
  const blob = new Blob(chunks.slice(), { type: blobType });

  const url = URL.createObjectURL(blob);
  const durationSeconds = elapsedSeconds.value;

  emit('recorded', {
    url,
    blob,
    durationSeconds,
    key: null,
    mime: blob.type || null,
    size: blob.size ?? null,
  });

  cleanup();
  emit('close');
};



// ─────────────────────────────────────────────
// Waveform rendering
// ─────────────────────────────────────────────
const MIN_BAR_HEIGHT = 10;
let previousBarHeights = [];

const startWaveformLoop = () => {
  const canvas = waveformCanvas.value;
  if (!canvas || !analyser.value || !dataArray.value) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  if (!previousBarHeights.length || previousBarHeights.length !== bufferLength.value) {
    previousBarHeights = new Array(bufferLength.value).fill(0);
  }

  const barWidth = 10;
  const spacing = 12;

  const render = () => {
    animationId = requestAnimationFrame(render);

    if (!analyser.value || !dataArray.value) return;

    analyser.value.getByteFrequencyData(dataArray.value);

    ctx.fillStyle = 'rgb(39, 39, 42)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let x = 0;
    for (let i = 0; i < bufferLength.value; i++) {
      const val = dataArray.value[i];
      let targetHeight = Math.max(val / 2, MIN_BAR_HEIGHT);
      const smoothed = targetHeight * 0.6 + previousBarHeights[i] * 0.4;
      previousBarHeights[i] = smoothed;

      const startY = canvas.height / 2 - smoothed / 2;
      const endY = canvas.height / 2 + smoothed / 2;

      const gradient = ctx.createLinearGradient(0, startY, 0, endY);
      gradient.addColorStop(0, 'rgb(50,150,220)');
      gradient.addColorStop(1, 'rgb(71,188,255)');
      ctx.fillStyle = gradient;

      ctx.beginPath();
      ctx.moveTo(x, endY);
      ctx.arcTo(x + barWidth, endY, x + barWidth, startY, barWidth / 2);
      ctx.arcTo(x + barWidth, startY, x, startY, barWidth / 2);
      ctx.arcTo(x, startY, x, endY, barWidth / 2);
      ctx.closePath();
      ctx.fill();

      x += barWidth + spacing;
    }
  };

  render();
};

// ─────────────────────────────────────────────
// Timer
// ─────────────────────────────────────────────
const startTimer = () => {
  recordingStartTime.value = Date.now();
  elapsedSeconds.value = 0;

  if (timerId) {
    clearInterval(timerId);
  }

  timerId = setInterval(() => {
    elapsedSeconds.value = Math.floor(
      (Date.now() - recordingStartTime.value) / 1000
    );
  }, 1000);
};

const stopTimer = () => {
  recordingStartTime.value = 0;
  elapsedSeconds.value = 0;
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
};

// ─────────────────────────────────────────────
// Cleanup
// ─────────────────────────────────────────────
const cleanupStreamsOnly = () => {
  if (mediaStream) {
    try {
      mediaStream.getTracks().forEach((t) => t.stop());
    } catch (e) {
      // ignore
    }
    mediaStream = null;
  }

  if (audioContext.value) {
    try {
      audioContext.value.close();
    } catch (e) {
      // ignore
    }
    audioContext.value = null;
    analyser.value = null;
    dataArray.value = null;
    bufferLength.value = 0;
    previousBarHeights = [];
  }
};

const cleanup = () => {
  stopTimer();

  if (animationId != null) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }

  if (mediaRecorder) {
    try {
      if (mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
    } catch (e) {
      // ignore
    }
    mediaRecorder = null;
  }

  cleanupStreamsOnly();

  chunks.length = 0;
  isRecording.value = false;
  micError.value = '';
};

onBeforeUnmount(() => {
  cleanup();
});

// ─────────────────────────────────────────────
// Button handlers
// ─────────────────────────────────────────────
const handleCancel = () => {
  // we are explicitly cancelling; no memo should be emitted
  wasCancelled.value = true;

  if (isRecording.value) {
    // we don't want to emit memo, so clear chunks
    chunks.length = 0;
    stopRecordingInternal();
  } else {
    cleanup();
    emit('close');
  }
};


const handleStop = () => {
  stopRecordingInternal();
};
</script>




